import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';

const TypingAnimationWithAudio = ({ question, answer, delay = 0 }) => {
  const [displayedQuestion, setDisplayedQuestion] = useState('');
  const [displayedAnswer, setDisplayedAnswer] = useState('');
  const [phase, setPhase] = useState('waiting');
  const [isMuted, setIsMuted] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const ref = useRef(null);
  const questionAudioRef = useRef(null);
  const answerAudioRef = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const hasAnimated = useRef(false);

  // Preload audios
  useEffect(() => {
    questionAudioRef.current = new Audio('/audio/wow-question.mp3');
    answerAudioRef.current = new Audio('/audio/wow-answer.mp3');
    questionAudioRef.current.preload = 'auto';
    answerAudioRef.current.preload = 'auto';
  }, []);

  // Start animation when in view
  useEffect(() => {
    if (isInView && !hasAnimated.current) {
      hasAnimated.current = true;
      setDisplayedQuestion('');
      setDisplayedAnswer('');
      setPhase('waiting');
      
      const timeout = setTimeout(() => {
        setPhase('typing-question');
        if (!isMuted && questionAudioRef.current) {
          questionAudioRef.current.play().catch(() => {});
        }
      }, delay);
      
      return () => clearTimeout(timeout);
    }
  }, [isInView, delay, isMuted]);

  // Typing animation
  useEffect(() => {
    let intervalId;

    if (phase === 'typing-question') {
      let index = 0;
      intervalId = setInterval(() => {
        if (index < question.length) {
          setDisplayedQuestion(question.slice(0, index + 1));
          index++;
        } else {
          clearInterval(intervalId);
          setTimeout(() => setPhase('pause'), 500);
        }
      }, 40);
    }

    if (phase === 'pause') {
      setTimeout(() => {
        setPhase('typing-answer');
        if (!isMuted && answerAudioRef.current) {
          answerAudioRef.current.play().catch(() => {});
        }
      }, 800);
    }

    if (phase === 'typing-answer') {
      let index = 0;
      intervalId = setInterval(() => {
        if (index < answer.length) {
          setDisplayedAnswer(answer.slice(0, index + 1));
          index++;
        } else {
          clearInterval(intervalId);
          setPhase('done');
        }
      }, 15);
    }

    return () => clearInterval(intervalId);
  }, [phase, question, answer, isMuted]);

  const toggleSound = () => {
    setHasInteracted(true);
    setIsMuted(!isMuted);
    
    if (isMuted) {
      // Turning sound ON - replay from current phase
      if (phase === 'typing-question' && questionAudioRef.current) {
        questionAudioRef.current.currentTime = 0;
        questionAudioRef.current.play().catch(() => {});
      } else if ((phase === 'typing-answer' || phase === 'done') && answerAudioRef.current) {
        answerAudioRef.current.currentTime = 0;
        answerAudioRef.current.play().catch(() => {});
      }
    } else {
      // Turning sound OFF
      questionAudioRef.current?.pause();
      answerAudioRef.current?.pause();
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-[#0d0d12] border border-white/10 rounded-2xl p-6 md:p-8 max-w-3xl mx-auto relative overflow-hidden"
    >
      {/* Sound toggle */}
      <motion.button
        onClick={toggleSound}
        className="absolute top-4 right-4 p-2 rounded-full bg-[#1a1a2e]/80 border border-white/10 hover:bg-[#1a1a2e] transition-colors z-10"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        animate={!hasInteracted ? {
          boxShadow: ['0 0 0 rgba(59, 130, 246, 0)', '0 0 12px rgba(59, 130, 246, 0.5)', '0 0 0 rgba(59, 130, 246, 0)']
        } : {}}
        transition={{ duration: 2, repeat: !hasInteracted ? Infinity : 0 }}
      >
        {isMuted ? (
          <VolumeX className="w-4 h-4 text-[#a1a1aa]" />
        ) : (
          <Volume2 className="w-4 h-4 text-[#3b82f6]" />
        )}
      </motion.button>

      {/* Terminal header */}
      <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/10">
        <motion.div 
          className="w-3 h-3 rounded-full bg-red-500"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        />
        <motion.div 
          className="w-3 h-3 rounded-full bg-yellow-500"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1, delay: 0.2 }}
        />
        <motion.div 
          className="w-3 h-3 rounded-full bg-green-500"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1, delay: 0.4 }}
        />
        <span className="ml-4 text-sm text-[#71717a] font-mono">cloneia</span>
        <motion.div
          className="ml-auto w-2 h-2 rounded-full bg-green-500 mr-10"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      </div>

      {/* Question */}
      <div className="mb-6">
        <span className="text-[#3b82f6] font-mono mr-2">&gt;&gt;</span>
        <span className="text-[#f5f5f5] font-medium">
          {displayedQuestion}
          {(phase === 'waiting' || phase === 'typing-question') && (
            <motion.span 
              className="inline-block w-[2px] h-5 bg-[#3b82f6] ml-1 align-middle"
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
          )}
        </span>
      </div>

      {/* Answer */}
      {(phase === 'typing-answer' || phase === 'done') && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="pl-6 text-[#a1a1aa] leading-relaxed"
        >
          {displayedAnswer}
          {phase === 'typing-answer' && (
            <motion.span 
              className="inline-block w-[2px] h-4 bg-[#3b82f6] ml-1 align-middle"
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
          )}
        </motion.div>
      )}

      {/* Glow on completion */}
      {phase === 'done' && (
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          initial={{ boxShadow: '0 0 0 rgba(59, 130, 246, 0)' }}
          animate={{ boxShadow: ['0 0 20px rgba(59, 130, 246, 0.3)', '0 0 0 rgba(59, 130, 246, 0)'] }}
          transition={{ duration: 1.5 }}
        />
      )}
    </motion.div>
  );
};

export default TypingAnimationWithAudio;
